import { DeleteOutlined, FilePdfOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { App, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../api/apiClient";
import { createDraft, submitDraft, updateDraft, type EntryDraftRecord } from "../../api/draftApi";
import { createBillEntry, getBillEntry, getBillEntryPdf, getBillLedgerBalance, getBillPartyBalance, previewBillEntry } from "../../api/entryApi";
import { fetchLookup } from "../../api/lookupApi";
import { AttachmentPanel } from "../../shared/components/AttachmentPanel";
import { PersistedTable } from "../../shared/components/PersistedTable";
import { SmartLookupField } from "../../shared/lookup/SmartLookupField";
import { formatInr } from "../../shared/utils/currency";
import { UI_DATE_FORMAT } from "../../shared/utils/date";
import { unwrapApiResponse } from "../../shared/utils/apiResponse";
import { balanceText } from "./voucherEntryUtils";
import {
  buildPayload,
  extractId,
  newLine,
  unitOptions,
  type BillLineRow,
  type GridMetaField,
  type PreviewInfo,
  type WorkbenchVoucherKind
} from "./billEntryWorkbenchUtils";
import { InvoicePdfOptionsModal } from "./InvoicePdfOptionsModal";

export function BillEntryWorkbench({
  voucherType,
  title,
  onSaved,
  draft,
  onDraftSaved,
  initialValues
}: {
  voucherType: WorkbenchVoucherKind;
  title: string;
  onSaved: (result?: { entityId?: number | null; bill?: any }) => void;
  draft?: EntryDraftRecord | null;
  onDraftSaved?: () => void;
  initialValues?: {
    partyId?: string | number | null;
    partyLabel?: string;
    referenceNo?: string;
    narration?: string;
  };
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [lines, setLines] = useState<BillLineRow[]>([newLine()]);
  const [partyLabel, setPartyLabel] = useState<string>();
  const [selectedPartyId, setSelectedPartyId] = useState<string | number | null>(null);
  const [partyBalanceOverride, setPartyBalanceOverride] = useState<{ closingBalance?: number; balanceType?: string } | null>(null);
  const [ledgerLabel, setLedgerLabel] = useState<string>();
  const [preview, setPreview] = useState<PreviewInfo | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pdfOptionsOpen, setPdfOptionsOpen] = useState(false);
  const tempKey = useMemo(() => crypto.randomUUID(), []);
  const watchedParty = Form.useWatch("partyId", form);
  const fixedLedgerLookupUrl = voucherType === "PurchaseInvoice" ? "/ledgers/lookup?category=Purchase" : "/ledgers/lookup?category=Sales";

  const metaQuery = useQuery({
    queryKey: ["bill-input-grid-meta"],
    queryFn: async () => {
      const { data } = await apiClient.get("/form-meta/bill-input-grid");
      const meta = unwrapApiResponse<{ fields?: GridMetaField[] }>(data);
      return meta.fields ?? [];
    }
  });
  const fixedLedgerQuery = useQuery({
    queryKey: ["bill-entry-fixed-ledger", voucherType],
    queryFn: () => fetchLookup(fixedLedgerLookupUrl),
    staleTime: Infinity
  });
  const partyBalance = useQuery({
    queryKey: ["bill-entry-workbench-party-balance", selectedPartyId ?? extractId(watchedParty)],
    enabled: !!(selectedPartyId ?? extractId(watchedParty)),
    queryFn: () => getBillPartyBalance<any>((selectedPartyId ?? extractId(watchedParty))!)
  });
  const ledgerBalance = useQuery({
    queryKey: ["bill-entry-workbench-ledger-balance", fixedLedgerQuery.data?.[0]?.id],
    enabled: !!fixedLedgerQuery.data?.[0]?.id,
    queryFn: () => getBillLedgerBalance<any>(fixedLedgerQuery.data?.[0]?.id as string | number)
  });

  const dynamicFields = (metaQuery.data ?? []).filter((field) => field.isDynamic);
  const gstRateOptions = useMemo(() => [0, 5, 12, 18, 28].map((value) => ({ value, label: `${value}%` })), []);
  const tdsRateOptions = useMemo(() => [0, 1, 2, 5, 10].map((value) => ({ value, label: `${value}%` })), []);
  const totals = useMemo(() => {
    const taxable = lines.reduce((sum, row) => sum + lineAmount(row), 0);
    // A negative line is a deduction from the same taxable basis. Its taxes must
    // therefore reduce the document tax totals instead of being silently ignored.
    const gst = lines.reduce((sum, row) => {
      const amount = lineAmount(row);
      return sum + (amount * Number(row.gstPercent ?? 0)) / 100;
    }, 0);
    const tds = lines.reduce((sum, row) => {
      const amount = lineAmount(row);
      return sum + (amount * Number(row.tdsPercent ?? 0)) / 100;
    }, 0);
    return { taxable, gst, tds, grand: taxable + gst - tds };
  }, [lines]);
  const localTaxPreview = useMemo(() => {
    const intraStateGst = totals.gst;
    const cgst = intraStateGst > 0 ? Number((intraStateGst / 2).toFixed(2)) : 0;
    const sgst = intraStateGst > 0 ? Number((intraStateGst - cgst).toFixed(2)) : 0;
    return { cgst, sgst, igst: 0 };
  }, [totals.gst]);
  const hasDeduction = useMemo(() => lines.some((row) => lineAmount(row) < 0), [lines]);
  const resolvedPartyBalance = useMemo(
    () => pickPreferredBalance(partyBalance.data, partyBalanceOverride ?? undefined),
    [partyBalance.data, partyBalanceOverride]
  );

  useEffect(() => {
    if (!draft) {
      form.setFieldsValue({
        billDate: dayjs(),
        dueDate: undefined,
        billingMonth: dayjs().format("MMMM"),
        voucherType,
        adjustmentMethod: "NewRef",
        partyId: initialValues?.partyId ?? undefined,
        referenceNo: initialValues?.referenceNo,
        narration: initialValues?.narration
      });
      setPartyLabel(initialValues?.partyLabel);
      setSelectedPartyId(extractId(initialValues?.partyId));
      setPartyBalanceOverride(null);
    }
  }, [draft, form, initialValues?.narration, initialValues?.partyId, initialValues?.partyLabel, initialValues?.referenceNo, voucherType]);

  useEffect(() => {
    if (!draft?.payload) return;
    const values = draft.payload.values ?? {};
    form.resetFields();
    form.setFieldsValue({
      ...values,
      billDate: values.billDate ? dayjs(values.billDate) : dayjs(),
      dueDate: values.dueDate ? dayjs(values.dueDate) : undefined,
      billingMonth: values.billingMonth ?? dayjs().format("MMMM"),
      voucherType
    });
    setPartyLabel(values.partyLabel);
    setSelectedPartyId(extractId(values.partyId));
    setPartyBalanceOverride(null);
    setLedgerLabel(values.ledgerLabel);
    setLines(Array.isArray(draft.payload.lines) && draft.payload.lines.length
      ? draft.payload.lines.map((line: BillLineRow) => ({ ...newLine(), ...line, amount: Number(line.amount ?? 0) || (Number(line.quantity ?? 0) * Number(line.rate ?? 0)), key: crypto.randomUUID() }))
      : [newLine()]);
    setDirty(true);
  }, [draft, form, voucherType]);

  useEffect(() => {
    const fixedLedger = fixedLedgerQuery.data?.[0];
    if (!fixedLedger) return;
    form.setFieldValue("ledgerAccountId", fixedLedger.id);
    setLedgerLabel(fixedLedger.label);
  }, [fixedLedgerQuery.data, form]);

  useEffect(() => {
    const previewLines = lines.filter(hasPreviewableBillLine);
    if (!extractId(watchedParty) || !previewLines.length) {
      setPreview(null);
      setPreviewLoading(false);
      return;
    }
    const handle = window.setTimeout(() => {
      setPreviewLoading(true);
      previewBillEntry<PreviewInfo>(buildPayload({
        values: form.getFieldsValue(),
        lines: previewLines,
        voucherType,
        tempKey
      }))
        .then(setPreview)
        .catch(() => undefined)
        .finally(() => setPreviewLoading(false));
    }, 450);
    return () => {
      window.clearTimeout(handle);
      setPreviewLoading(false);
    };
  }, [form, lines, tempKey, voucherType, watchedParty]);

  const reset = () => {
    form.resetFields();
    form.setFieldsValue({
      billDate: dayjs(),
      dueDate: undefined,
      billingMonth: dayjs().format("MMMM"),
      voucherType,
      adjustmentMethod: "NewRef",
      ledgerAccountId: fixedLedgerQuery.data?.[0]?.id,
      partyId: initialValues?.partyId ?? undefined,
      referenceNo: initialValues?.referenceNo,
      narration: initialValues?.narration
    });
    setLines([newLine()]);
    setPartyLabel(initialValues?.partyLabel);
    setSelectedPartyId(extractId(initialValues?.partyId));
    setPartyBalanceOverride(null);
    setLedgerLabel(fixedLedgerQuery.data?.[0]?.label);
    setPreview(null);
    setDirty(false);
  };

  const updateLine = (key: string, patch: Partial<BillLineRow>, source?: "quantity" | "rate" | "amount") => {
    setDirty(true);
    setLines((current) => current.map((line) => {
      if (line.key !== key) return line;
      const next: BillLineRow = { ...line, ...patch };
      const quantity = Number(next.quantity ?? 0);
      const rate = Number(next.rate ?? 0);
      const amount = Number(next.amount ?? 0);

      if (source === "amount") {
        next.amount = amount;
        if (quantity > 0) next.rate = Number((amount / quantity).toFixed(6));
        return next;
      }

      if (source === "rate") {
        next.rate = rate;
        if (quantity > 0) next.amount = Number((quantity * rate).toFixed(2));
        return next;
      }

      if (source === "quantity") {
        next.quantity = quantity;
        if (quantity > 0 && amount > 0 && rate === 0) next.rate = Number((amount / quantity).toFixed(6));
        if (quantity > 0 && Number(next.rate ?? 0) !== 0) next.amount = Number((quantity * Number(next.rate ?? 0)).toFixed(2));
        return next;
      }

      if (quantity > 0 && Number(next.rate ?? 0) !== 0) next.amount = Number((quantity * Number(next.rate ?? 0)).toFixed(2));
      return next;
    }));
  };

  const saveDraft = async () => {
    const values = form.getFieldsValue();
    const payload = {
      values: {
        ...values,
        billDate: values.billDate?.format?.("YYYY-MM-DD"),
        partyLabel,
        ledgerLabel
      },
      lines
    };
    const draftTitle = `${values.billDate?.format?.("YYYY-MM-DD") ?? dayjs().format("YYYY-MM-DD")} - ${partyLabel || title}`;
    try {
      if (draft) await updateDraft(draft.id, { moduleKey: voucherType, title: draftTitle, payload });
      else await createDraft({ moduleKey: voucherType, title: draftTitle, payload });
      setDirty(false);
      onDraftSaved?.();
      message.success("Draft saved");
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? "Unable to save draft");
    }
  };

  const saveBill = async () => {
    await persistBill(false);
  };

  const saveAndGeneratePdf = () => {
    setPdfOptionsOpen(true);
  };

  const persistBill = async (generatePdf: boolean, roundToNearestRupee = true) => {
    const values = await form.validateFields();
    const validLines = lines.filter((line) =>
      line.ledgerName ||
      line.sacCode ||
      Number(line.quantity ?? 0) !== 0 ||
      Number(line.rate ?? 0) !== 0 ||
      Number(line.amount ?? 0) !== 0 ||
      line.remarks
    );
    if (!validLines.length) {
      message.warning("Add at least one line item.");
      return;
    }
    const lineErrors = validLines.flatMap((line, index) => invoiceLineErrors(line, index + 1));
    if (lineErrors.length) {
      Modal.warning({
        title: "Complete the line items before posting",
        content: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {lineErrors.map((error) => <li key={error}>{error}</li>)}
          </ul>
        )
      });
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload({ values, lines: validLines, voucherType, tempKey });
      const entityId = draft
        ? (await submitDraft(draft.id, payload))?.entityId ?? null
        : await createBillEntry(payload);
      let savedBill: any = null;

      if (entityId) {
        try {
          savedBill = await getBillEntry<any>(entityId);
        } catch {
          savedBill = null;
        }
      }

      if (generatePdf && entityId) {
        const blob = await getBillEntryPdf(entityId, roundToNearestRupee);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }

      message.success(generatePdf ? `${title} saved and PDF generated` : `${title} saved`);
      if (generatePdf) setPdfOptionsOpen(false);
      reset();
      onSaved({ entityId, bill: savedBill });
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? `Unable to ${generatePdf ? "generate PDF for" : "save"} ${title}`);
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<BillLineRow> = [
    { title: "#", width: 34, render: (_, __, index) => index + 1 },
    {
      title: "Ledger",
      dataIndex: "ledgerName",
      width: 210,
      render: (_, row) => (
        <SmartLookupField
          lookupKey="accountLedger"
          label={row.ledgerName}
          onChange={(value) => updateLine(row.key, { ledgerName: value.label })}
        />
      )
    },
    {
      title: "HSN/SAC",
      dataIndex: "sacCode",
      width: 118,
      render: (_, row) => (
        <SmartLookupField
          lookupKey="sacTaxProfile"
          label={row.sacCode}
          onChange={(value) => updateLine(row.key, { sacCode: String(value.id ?? value.label ?? "") })}
        />
      )
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      width: 88,
      render: (_, row) => <InputNumber value={row.quantity} min={0} onChange={(value) => updateLine(row.key, { quantity: Number(value ?? 0) }, "quantity")} style={{ width: "100%" }} />
    },
    {
      title: "Rate",
      dataIndex: "rate",
      width: 106,
      render: (_, row) => <InputNumber value={row.rate} onChange={(value) => updateLine(row.key, { rate: Number(value ?? 0) }, "rate")} style={{ width: "100%" }} />
    },
    {
      title: "Amount",
      width: 112,
      render: (_, row) => <InputNumber value={row.amount} onChange={(value) => updateLine(row.key, { amount: Number(value ?? 0) }, "amount")} style={{ width: "100%" }} />
    },
    {
      title: "Per",
      dataIndex: "per",
      width: 68,
      render: (_, row) => (
        <Select value={row.per} options={unitOptions} showSearch={false} onChange={(value) => updateLine(row.key, { per: String(value ?? "") })} />
      )
    },
    ...dynamicFields.map((field) => ({
      title: field.label,
      width: 96,
      render: (_: unknown, row: BillLineRow) => (
        <InputNumber
          value={row.dynamicValues[field.defCode ?? field.name] ?? 0}
          onChange={(value) => updateLine(row.key, {
            dynamicValues: {
              ...row.dynamicValues,
              [field.defCode ?? field.name]: Number(value ?? 0)
            }
          })}
          style={{ width: "100%" }}
        />
      )
    })),
    {
      title: "GST %",
      dataIndex: "gstPercent",
      width: 88,
      render: (_, row) => <Select value={row.gstPercent} options={gstRateOptions} onChange={(value) => updateLine(row.key, { gstPercent: Number(value ?? 0) })} />
    },
    {
      title: "TDS %",
      dataIndex: "tdsPercent",
      width: 88,
      render: (_, row) => <Select value={row.tdsPercent} options={tdsRateOptions} onChange={(value) => updateLine(row.key, { tdsPercent: Number(value ?? 0) })} />
    },
    {
      key: "actions",
      title: " ",
      width: 52,
      render: (_, row) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          disabled={lines.length === 1}
          onClick={() => {
            if (lines.length === 1) return;
            setDirty(true);
            setLines((current) => current.filter((line) => line.key !== row.key));
          }}
        />
      )
    }
  ];

  return (
    <div className="tally-vc bill-entry-inline-workbench">
      <div className="tally-header">
        <div className="tally-hdr-left">
          <span style={{ fontSize: "0.9rem", fontWeight: 700, color: "#1b2b5e", whiteSpace: "nowrap" }}>
            {voucherType === "PurchaseInvoice" ? "Purchase Invoice" : "Sales Invoice"}
          </span>
          <span className="tally-no-lbl">No.</span>
          <Form form={form} component={false}>
            <Form.Item name="billNo" noStyle>
              <Input className="tally-billno-input" placeholder="Bill No." bordered={false} />
            </Form.Item>
          </Form>
        </div>
        <div className="tally-hdr-right">
          <div className="tally-date-line">
            <Form form={form} component={false}>
              <Form.Item name="billDate" noStyle rules={[{ required: true }]}>
                <DatePicker format={UI_DATE_FORMAT} style={{ width: 130 }} />
              </Form.Item>
            </Form>
          </div>
          <div className="tally-hdr-actions">
            <Button size="small" onClick={reset}>New Bill</Button>
            <Button size="small" onClick={saveDraft}>Save Draft</Button>
            {voucherType === "SalesInvoice" ? (
              <Button size="small" icon={<FilePdfOutlined />} loading={saving} onClick={saveAndGeneratePdf}>
                Save &amp; Generate PDF
              </Button>
            ) : null}
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveBill}>Post</Button>
          </div>
        </div>
      </div>

      <div className="tally-layout">
        <div className="tally-main">
          <Form form={form} layout="vertical" onValuesChange={() => setDirty(true)}>
            <div className="tally-row">
              <span className="tally-lbl">{voucherType === "PurchaseInvoice" ? "Supplier Invoice No." : "Customer Ref No."}</span>
              <Form.Item name="referenceNo" noStyle>
                <Input placeholder="Optional" bordered={false} style={{ width: 180 }} />
              </Form.Item>
              <span className="tally-lbl" style={{ width: "auto", marginLeft: 16, flexShrink: 0 }}>Due Date</span>
              <Form.Item name="dueDate" noStyle>
                <DatePicker format={UI_DATE_FORMAT} style={{ width: 145 }} />
              </Form.Item>
            </div>

            <div className="tally-sep" />

            <div className="tally-row">
              <span className="tally-lbl">Party A/c name</span>
              <Form.Item name="partyId" noStyle rules={[{ required: true, message: "Party is required" }]}>
                <SmartLookupField lookupKey="party" label={partyLabel} onChange={(value) => {
                  form.setFieldValue("partyId", value.id);
                  setPartyLabel(value.label);
                  setSelectedPartyId(value.id);
                  const raw = value.raw as Record<string, unknown> | undefined;
                  const debitTotal = Number(raw?.DebitTotal ?? raw?.debitTotal ?? 0);
                  const creditTotal = Number(raw?.CreditTotal ?? raw?.creditTotal ?? 0);
                  const explicitClosingBalance = Number(raw?.ClosingBalance ?? raw?.closingBalance ?? raw?.Balance ?? raw?.balance ?? 0);
                  if (debitTotal !== 0 || creditTotal !== 0) {
                    const closingBalance = debitTotal - creditTotal;
                    setPartyBalanceOverride({ closingBalance, balanceType: closingBalance >= 0 ? "Dr" : "Cr" });
                  } else if (explicitClosingBalance !== 0) {
                    setPartyBalanceOverride({
                      closingBalance: explicitClosingBalance,
                      balanceType: explicitClosingBalance >= 0 ? "Dr" : "Cr"
                    });
                  } else {
                    setPartyBalanceOverride(null);
                  }
                }} />
              </Form.Item>
            </div>
            <div className="tally-row tally-row-balance">
              <span className="tally-lbl" style={{ color: "#777" }}>Current balance</span>
              <span className="tally-balance-val">{balanceText(resolvedPartyBalance)}</span>
            </div>

            <div className="tally-row">
              <span className="tally-lbl">{voucherType === "PurchaseInvoice" ? "Purchase ledger" : "Sales ledger"}</span>
              <Form.Item name="ledgerAccountId" hidden><Input /></Form.Item>
              <Input readOnly bordered={false} value={ledgerLabel ?? fixedLedgerQuery.data?.[0]?.label ?? ""} />
            </div>
            <div className="tally-row tally-row-balance">
              <span className="tally-lbl" style={{ color: "#777" }}>Current balance</span>
              <span className="tally-balance-val">{balanceText(ledgerBalance.data)}</span>
            </div>

            <div className="tally-sep" />
            <Form.Item name="billingMonth" hidden><Input /></Form.Item>
            <Form.Item name="sacCode" hidden><Input /></Form.Item>
          </Form>

          <div className="tally-grid-wrap">
            <PersistedTable
              tableId={`bill-entry-workbench-v2-${voucherType}`}
              rowKey="key"
              size="small"
              bordered
              pagination={false}
              columns={columns}
              dataSource={lines}
              rowClassName={(row) => lineAmount(row) < 0 ? "tally-deduction" : ""}
            />
          </div>

          <div className="tally-add-row">
            <button type="button" className="tally-add-btn" onClick={() => { setDirty(true); setLines((current) => [...current, newLine()]); }}>
              + Add Line
            </button>
          </div>

          {hasDeduction ? (
            <div className="bill-deduction-note">
              Negative amount detected. This line will be treated as a deduction.
            </div>
          ) : null}

          <div className="tally-tax-section">
            {previewLoading ? (
              <div className="bill-preview-sync">
                <Spin size="small" />
                <span>Refreshing totals...</span>
              </div>
            ) : null}
            <div className="tally-tax-row">
              <span className="tally-tax-lbl">Sub Total</span>
              <span className="tally-tax-amt">{formatInr(preview?.taxableAmount ?? totals.taxable)}</span>
            </div>
            <div className="tally-tax-row">
              <span className="tally-tax-lbl">CGST</span>
              <span className="tally-tax-amt">{formatInr(preview?.cgstTotal ?? localTaxPreview.cgst)}</span>
            </div>
            <div className="tally-tax-row">
              <span className="tally-tax-lbl">SGST</span>
              <span className="tally-tax-amt">{formatInr(preview?.sgstTotal ?? localTaxPreview.sgst)}</span>
            </div>
            {(preview?.igstTotal ?? localTaxPreview.igst) !== 0 ? (
              <div className="tally-tax-row">
                <span className="tally-tax-lbl">IGST</span>
                <span className="tally-tax-amt">{formatInr(preview?.igstTotal ?? localTaxPreview.igst)}</span>
              </div>
            ) : null}
            <div className="tally-tax-row">
              <span className="tally-tax-lbl">TDS Deducted</span>
              <span className="tally-tax-amt">{(preview?.tdsTotal ?? totals.tds) !== 0 ? formatInr(preview?.tdsTotal ?? totals.tds) : "\u2014"}</span>
            </div>
            <div className="tally-tax-row">
              <span className="tally-tax-lbl">Payable Amount</span>
              <span className="tally-tax-amt">{formatInr(preview?.grandTotal ?? totals.grand)}</span>
            </div>
          </div>

          <div className="tally-narration-wrap">
            <span className="tally-narration-lbl">Narration:</span>
            <Form form={form} layout="vertical" component={false}>
              <Form.Item name="narration" style={{ marginBottom: 0, flex: 1 }}>
                <Input className="tally-narration-input" bordered={false} placeholder="Optional narration..." />
              </Form.Item>
            </Form>
            <div className="tally-grand-wrap">
              <strong className="tally-grand-val">{formatInr(preview?.grandTotal ?? totals.grand)}</strong>
            </div>
          </div>

          <div style={{ padding: "10px" }}>
            <AttachmentPanel module="BillEntry" tempKey={tempKey} compact />
          </div>
          {dirty ? <div className="bill-workbench-dirty">Unsaved bill changes</div> : null}
        </div>
      </div>
      <InvoicePdfOptionsModal
        open={pdfOptionsOpen}
        loading={saving}
        onCancel={() => setPdfOptionsOpen(false)}
        onGenerate={(roundToNearestRupee) => persistBill(true, roundToNearestRupee)}
      />
    </div>
  );
}

function TotalTile({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className={`bill-total-tile${strong ? " is-strong" : ""}`}>
      <span>{label}</span>
      <strong>{formatInr(value)}</strong>
    </div>
  );
}

function lineAmount(row: BillLineRow) {
  const explicitAmount = Number(row.amount ?? 0);
  if (explicitAmount !== 0) return explicitAmount;
  return Number(row.quantity ?? 0) * Number(row.rate ?? 0);
}

function hasPreviewableBillLine(row: BillLineRow) {
  const quantity = Number(row.quantity ?? 0);
  const rate = Number(row.rate ?? 0);
  const amount = Number(row.amount ?? 0);
  const gstPercent = Number(row.gstPercent ?? 0);
  const tdsPercent = Number(row.tdsPercent ?? 0);
  const hasDynamicValue = Object.values(row.dynamicValues ?? {}).some((value) => Number(value ?? 0) !== 0);
  return Boolean(
    row.ledgerName
    || row.sacCode
    || quantity !== 0
    || rate !== 0
    || amount !== 0
    || gstPercent !== 0
    || tdsPercent !== 0
    || hasDynamicValue
  );
}

function invoiceLineErrors(row: BillLineRow, rowNumber: number) {
  const amount = lineAmount(row);
  const isDeduction = amount < 0;
  const issues: string[] = [];
  if (!row.ledgerName) issues.push("ledger is missing");
  if (!row.sacCode) issues.push("HSN/SAC is missing");
  if (amount === 0) issues.push("amount must be non-zero");
  if (!isDeduction && Number(row.quantity ?? 0) > 0 && !row.per) issues.push("unit is missing");
  return issues.length ? [`Row ${rowNumber}: ${issues.join(", ")}`] : [];
}

function pickPreferredBalance(
  primary?: { closingBalance?: number; ClosingBalance?: number; balance?: number; Balance?: number; balanceType?: string; BalanceType?: string; type?: string; Type?: string } | null,
  fallback?: { closingBalance?: number; ClosingBalance?: number; balance?: number; Balance?: number; balanceType?: string; BalanceType?: string; type?: string; Type?: string } | null
) {
  const primaryValue = Number(primary?.closingBalance ?? primary?.ClosingBalance ?? primary?.balance ?? primary?.Balance ?? 0);
  const fallbackValue = Number(fallback?.closingBalance ?? fallback?.ClosingBalance ?? fallback?.balance ?? fallback?.Balance ?? 0);
  if (primary && primaryValue !== 0) return primary;
  if (fallback && fallbackValue !== 0) return fallback;
  return primary ?? fallback ?? undefined;
}
